import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { isPresent } from '@ember/utils';
import { toLatLngObject, toCoords } from 'garaje/helpers/resource-coords';
import { POLYGON_BASE_OPTIONS, POLYGON_COLORS } from 'garaje/utils/polygon';
import {
  getCustomMarkerSize,
  getLegacyLiveMapMarkerSize,
  getMarkerShellBorderWidth,
  getRoomLabelGap,
  MARKER_SHELL_HIDE_CONTENT_AT_MARKER_SIZE,
} from 'garaje/utils/map-marker-scale';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import leaflet from 'leaflet';
import { AvailabilityStatus, ResourceType } from 'garaje/services/live-map';

/**
 * @param {Leaflet<Map>}                map
 * @param {Model<MapFeature>}           feature
 * @param {Array}                       mapDimensions
 * @param {Boolean}                     viewOnly
 * @param {Boolean}                     isVersionMap
 * @param {Model<MapFeature>}           selectedFeature
 * @param {Function}                    onClick
 * @param {Function}                    setIsOverArea // disable interaction to place resource on an area
 * @param {Function}                    onDragStart
 * @param {Function}                    onDrag
 */
export default class ResourceMapArea extends Component {
  @service featureFlags;
  @service featureConfig;
  @service store;
  @service abilities;
  @service liveMap;

  area;
  marker;
  tooltip;
  layerGroup;
  isEditing = false;
  featureLabelStyle = '';
  _onZoomEnd = null;

  isFiniteLayerPoint(point) {
    return Number.isFinite(point?.x) && Number.isFinite(point?.y);
  }

  get isLiveMapView() {
    return this.args.viewOnly && !this.args.isVersionMap;
  }

  get colorKey() {
    const { feature, viewOnly, isVersionMap } = this.args;
    // viewOnly means the current map view is not editable
    // isVersionMap means the 'Map Version' is in view

    const isLiveMap = viewOnly && !isVersionMap; // only show room status in live map and not version map

    if (feature.isNew || feature.hasDirtyAttributes || this.isEditing) {
      return 'edit';
    }
    // When using snapshot data, we want to check against the map-feature externalId since the room.id might not exist
    const roomStatus = this.liveMap.getResourceAvailabilityByIdAndType(feature.externalId, ResourceType.ROOM);
    if (roomStatus === AvailabilityStatus.OCCUPIED && isLiveMap) {
      return 'unavailable';
    }

    // If we are in the version or edit map, we want to show the room polygon color as green regardless of the status
    if (roomStatus === AvailabilityStatus.AVAILABLE || feature.enabled || isLiveMap) {
      return 'enabled';
    }

    return 'disabled';
  }

  get colorOptions() {
    const color = this.colorKey;
    const colors = POLYGON_COLORS[color];
    const polygonWeight = this.isLiveMapView ? 1.5 : POLYGON_BASE_OPTIONS.weight;

    if (!this.args.viewOnly && this.abilities.cannot('edit maps')) {
      return {
        color: `${colors.border}50`,
        fillColor: `${colors.fill}50`,
        weight: polygonWeight,
      };
    }
    return {
      color: colors.border,
      fillColor: colors.fill,
      weight: polygonWeight,
    };
  }

  get className() {
    const { feature, viewOnly, isVersionMap, selectedFeature } = this.args;
    const classes = ['resource-icon-base', feature.type, !feature.isValid || feature.hasError ? 'error' : null];
    const roomIsAvailable = this.roomIsAvailable(feature?.externalId);
    const isLiveMap = viewOnly && !isVersionMap;

    if ((feature.enabled && !viewOnly) || (isLiveMap && roomIsAvailable) || isVersionMap) {
      classes.push('is-enabled');
    }

    if (isLiveMap && !roomIsAvailable) {
      classes.push('is-unavailable');
    }

    if (feature.isNew) {
      classes.push('is-placing');
    }

    if (!feature.enabled) {
      classes.push('is-disabled');
    }

    if (viewOnly && feature === selectedFeature) {
      classes.push('is-selected');
    }

    if (!viewOnly && this.abilities.cannot('edit maps')) {
      classes.push('is-uneditable');
    }

    // Marker is below the hide-content threshold — matches the hide-content
    // behavior the point markers (desks, parking, POIs) honor via
    // getCircularMarkerShellHtml. The per-type class stays so CSS can still
    // pick the right colored shell; only the inner SVG glyph drops out.
    if (this.markerSize < MARKER_SHELL_HIDE_CONTENT_AT_MARKER_SIZE) {
      classes.push('is-glyph-hidden');
    }

    return classes.compact().join(' ');
  }

  get iconOptions() {
    return {
      iconSize: [this.markerSize, this.markerSize],
      className: this.className,
      html: this.featureConfig.isEnabled('mapsPointOfInterest') ? this.getRoomHtml() : '',
    };
  }

  get markerSize() {
    const { feature, viewOnly, selectedFeature } = this.args;
    const isSelected = viewOnly && feature === selectedFeature;

    if (!this.isLiveMapView) {
      return isSelected ? 60 : 30;
    }

    if (!this.featureFlags.isEnabled('customizable-marker-size')) {
      return getLegacyLiveMapMarkerSize({
        currentZoom: this.args.currentZoom,
        baseZoom: this.args.baseZoom,
        isSelected,
      });
    }

    return getCustomMarkerSize({
      legacyMarkerSizing: this.args.legacyMarkerSizing,
      currentMapWidth: this.args.currentMapImageWidth,
      markerSizeRatio: this.args.markerSizeRatio,
      currentZoom: this.args.currentZoom,
      baseZoom: this.args.baseZoom,
      isSelected,
    });
  }

  get roomLabelGap() {
    return getRoomLabelGap(this.args.currentZoom, this.args.baseZoom);
  }

  roomIsAvailable(externalId) {
    if (!externalId) {
      return false;
    }
    const roomStatus = this.liveMap.getResourceAvailabilityByIdAndType(externalId, ResourceType.ROOM);
    return roomStatus === AvailabilityStatus.AVAILABLE;
  }

  getLabelColor() {
    const { feature, viewOnly, isVersionMap } = this.args;

    const showViewOnlyColors = !isVersionMap && viewOnly;

    if (!showViewOnlyColors && this.abilities.cannot('edit maps')) {
      return feature.enabled ? 'cilantro-30' : 'carbon-30';
    }

    const roomIsAvailable = this.roomIsAvailable(feature?.externalId);

    if ((feature.enabled && !showViewOnlyColors) || (showViewOnlyColors && roomIsAvailable)) {
      return 'cilantro-40';
    }

    if (showViewOnlyColors && !roomIsAvailable) {
      return 'red-40';
    }

    return 'carbon-30';
  }

  getRoomHtml() {
    const { feature } = this.args;
    const labelColor = this.getLabelColor(this.args);
    const labelStyle = this.featureLabelStyle ? `style="${this.featureLabelStyle}"` : '';
    const labelHtml = `<div
          class="feature-label map-polygon-label absolute text-center text-${labelColor}" data-test-feature-label ${labelStyle}>
          ${feature.name || ''}
        </div>`;

    // Below the hide-content threshold, render an inline shell (border +
    // colored fill + shadow) instead of relying on the wrapper's CSS
    // background-image. The wrapper's per-type SVG and outer styling are
    // suppressed via `.is-glyph-hidden`/`.live-map-custom-shell` rules so
    // only the inline shell renders. Mirrors how point markers
    // (getCircularMarkerShellHtml) render at this size — same border, fill,
    // and shadow, so rooms visually match restrooms et al.
    if (this.markerSize < MARKER_SHELL_HIDE_CONTENT_AT_MARKER_SIZE) {
      const borderWidth = getMarkerShellBorderWidth(this.markerSize);
      const fill = this._smallShellBackgroundColor();
      const shellStyle = [
        `width:${this.markerSize}px`,
        `height:${this.markerSize}px`,
        `border:${borderWidth}px solid #ffffff`,
        `border-radius:9999px`,
        `box-sizing:border-box`,
        `background-color:${fill}`,
        `box-shadow:0 5.4px 10.7px 0 rgba(0, 0, 0, 0.12)`,
      ].join(';');

      return htmlSafe(`
        <div aria-hidden="true" data-test-feature-marker=${feature.type}>
          <div style="${shellStyle}"></div>
          ${labelHtml}
        </div>
      `);
    }

    return htmlSafe(`
      <div aria-hidden="true" data-test-feature-marker=${feature.type}>
        <div style="width:${this.markerSize}px;height:${this.markerSize}px;"></div>
        ${labelHtml}
      </div>
    `);
  }

  _smallShellBackgroundColor() {
    const { feature } = this.args;
    const isDisabled = !feature.enabled;
    const isUnavailable = this.isLiveMapView && !this.roomIsAvailable(feature?.externalId);

    if (isDisabled) return '#C9CBD4';
    if (isUnavailable) return '#FA4338';

    // Mirrors getCircularFeatureConfig() in resource-map/component.js. Drift
    // here is the cost of the dual rendering pipeline; tracked as the
    // follow-up refactor in the SCSS comment alongside .is-glyph-hidden.
    const colorByType = {
      room: '#4EC176',
      accessible: '#3F4450',
      elevator: '#3F4450',
      restroom: '#3F4450',
      stairs: '#3F4450',
      custom: '#3F4450',
      aed: '#FF8F33',
      'emergency-exit': '#FF8F33',
      'fire-extinguisher': '#FF8F33',
      'first-aid-kit': '#FF8F33',
      printer: '#4E4EDA',
      'break-room': '#4E4EDA',
      cafe: '#4E4EDA',
      kitchen: '#4E4EDA',
      'mothers-room': '#4E4EDA',
      'visitor-area': '#4E4EDA',
      'delivery-area': '#4E4EDA',
    };
    return colorByType[feature.type] || '#4EC176';
  }

  updateFeatureLabelStyle(area = this.area, map = this.args.map) {
    if (!area || !map) {
      this.featureLabelStyle = '';
      return;
    }

    const bounds = area.getBounds();
    const northWest = map.latLngToLayerPoint(bounds.getNorthWest());
    const southEast = map.latLngToLayerPoint(bounds.getSouthEast());

    if (!this.isFiniteLayerPoint(northWest) || !this.isFiniteLayerPoint(southEast)) {
      this.featureLabelStyle = '';
      return;
    }

    const polygonWidth = Math.abs(southEast.x - northWest.x);
    const polygonHeight = Math.abs(southEast.y - northWest.y);

    const labelWidth = Math.min(140, Math.max(56, Math.floor(polygonWidth - 8)));
    const maxLines = polygonHeight < 42 ? 1 : polygonHeight < 72 ? 2 : 3;
    const fontSize = labelWidth < 72 ? 14 : labelWidth < 96 ? 16 : 18;
    const lineHeight = fontSize + 4;

    this.featureLabelStyle = [
      `--feature-label-width:${labelWidth}px`,
      `--feature-label-max-lines:${maxLines}`,
      `--feature-label-font-size:${fontSize}px`,
      `--feature-label-line-height:${lineHeight}px`,
      `--feature-label-gap:${this.roomLabelGap}px`,
    ].join(';');
  }

  updateMarkerIcon() {
    this.updateFeatureLabelStyle();
    const icon = new leaflet.DivIcon(this.iconOptions);
    this.marker.setIcon(icon);
  }

  @action
  onInsert() {
    const { map } = this.args;
    this._appendArea(map);
  }

  @action
  onDestroy() {
    if (this._onZoomEnd && this.args.map) {
      this.args.map.off('zoomend', this._onZoomEnd);
    }
    this.layerGroup.remove();
  }

  @action
  onUpdate() {
    if (this.isEditing && !this.args.feature.hasDirtyAttributes) {
      this.isEditing = false;
      this.area.editing.disable();
    }

    this.area.setStyle(this.colorOptions);
    this.updateMarkerIcon();
  }

  @action
  updateIcon() {
    this.updateMarkerIcon();
  }

  @action
  updateZoomStyles() {
    this.updateMarkerIcon();
  }

  @action
  toggleEdit() {
    const { feature, selectedFeature, viewOnly, onDrag, onDragStart } = this.args;

    if (feature === selectedFeature && !viewOnly) {
      this.isEditing = true;
      this.area.editing.enable();

      const editingMakers = this.area.editing._verticesHandlers[0]._markerGroup.getLayers();
      editingMakers.forEach((marker) => {
        marker.on('dragstart', onDragStart);
        marker.on('drag', (e) => onDrag(e, this.area));
      });
    } else {
      this.isEditing = false;
      this.area.editing.disable();
    }

    this.updateMarkerIcon();
    this.area.setStyle(this.colorOptions);
  }

  @action
  updateTooltip() {
    this.tooltip.options.opacity = isPresent(this.args.feature.name) ? 1 : 0;
    this.tooltip.setContent(this.args.feature.name);
  }

  @action
  onAreaDragStart({ target }) {
    this.area.editing.disable();
    target._path.classList.add('is-dragging');
  }

  @action
  onAreaDrag({ target }, marker) {
    const [coords] = target.getLatLngs();
    marker.setLatLng(new leaflet.Polygon(coords).getBounds().getCenter()).update();
  }

  @action
  onAreaDragEnd({ target }, feature) {
    target._path.classList.remove('is-dragging');
    const [updatedCoords] = target.getLatLngs();

    if (this.args.bounds.contains(updatedCoords)) {
      this.updateFeatureCoordinates(feature, updatedCoords);
    } else {
      const coords = feature.geometry.coordinates.map((coordsArray) =>
        toLatLngObject(coordsArray, this.args.mapDimensions),
      );

      target.setLatLngs(coords);
      this.area.editing.initialize(target);
      this.marker.setLatLng(new leaflet.Polygon(coords).getBounds().getCenter()).update();
    }

    this.args.onClick({ target });
  }

  @action
  onEditDrag({ target }, feature, marker) {
    const [updatedCoords] = target.getLatLngs();
    this.updateFeatureCoordinates(feature, updatedCoords);

    marker.setLatLng(new leaflet.Polygon(updatedCoords).getBounds().getCenter()).update();
  }

  @action
  updateFeatureCoordinates(feature, coordinates) {
    const coords = coordinates.map((latLng) => {
      const { xPos, yPos } = toCoords(latLng, this.args.mapDimensions);
      return [xPos, yPos];
    });
    const geometry = { ...feature.geometry };

    geometry.coordinates = coords;
    set(feature, 'geometry', geometry);
  }

  @action
  onDeleted() {
    const { feature } = this.args;

    if (feature.isDeleted) this.layerGroup.remove();
  }

  _appendArea(map) {
    const { feature, viewOnly, onClick, setIsOverArea, cancelDraw } = this.args;
    const coords = feature.geometry.coordinates.map((coordsArray) =>
      toLatLngObject(coordsArray, this.args.mapDimensions),
    );
    const area = new leaflet.Polygon(coords, {
      ...this.colorOptions,
      ...POLYGON_BASE_OPTIONS,
      draggable: !viewOnly && this.abilities.can('edit maps'),
    });

    this.updateFeatureLabelStyle(area, map);
    const center = area.getBounds().getCenter();
    const icon = new leaflet.DivIcon(this.iconOptions);
    const marker = new leaflet.Marker(center, {
      icon,
      zIndexOffset: 10000,
    });
    const tooltip = new leaflet.Tooltip({
      opacity: isPresent(feature.name) ? 1 : 0,
      className: 'feature-tooltip',
    }).setContent(feature.name);

    marker.on('click', onClick).on('mousedown', cancelDraw).bindTooltip(tooltip);

    this._onZoomEnd = () => {
      this.updateFeatureLabelStyle();
      const updatedIcon = new leaflet.DivIcon(this.iconOptions);
      this.marker.setIcon(updatedIcon);
    };
    map.on('zoomend', this._onZoomEnd);

    if (!viewOnly && this.abilities.can('edit maps')) {
      area
        .on('mouseover', () => setIsOverArea(true))
        .on('mouseout', () => setIsOverArea(false))
        .on('dragstart', this.onAreaDragStart)
        .on('drag', (e) => this.onAreaDrag(e, marker))
        .on('dragend', (e) => this.onAreaDragEnd(e, feature))
        .on('editdrag', (e) => this.onEditDrag(e, feature, marker))
        .on('edit', (e) => this.onEditDrag(e, feature, marker));
    }

    this.area = area;
    this.marker = marker;
    this.tooltip = tooltip;
    const layers = [area, marker];

    this.layerGroup = new leaflet.LayerGroup(layers, { isArea: true, feature }).addTo(map);

    this._registerPolygonTestSelectors(area, feature);
    this._registerMarkerTestSelectors(marker, feature);
  }

  _registerPolygonTestSelectors({ _path }, feature) {
    _path.dataset.testPolygon = true;
    _path.dataset.testPolygonFeatureType = feature.type;
    _path.dataset.testPolygonFeatureId = feature.id;
  }

  _registerMarkerTestSelectors({ _icon }, feature) {
    _icon.dataset.testFeatureType = feature.type;
    _icon.dataset.testFeatureId = feature.id;
  }
}
